import {z} from "zod";

const SPECIAL_CHAR_REGEX = /[´ñáéíóúáèìòùâêîôûäëïöüãõ¨';[(){}¡!"ª°·#$%^&*+=\\|~<>¿?]/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;

/**
 * Builds a Zod string schema that validates emails using the project's
 * historical rules (no special chars, no consecutive dots/hyphens, etc.).
 * Accepts a translator `t` so messages are i18n-friendly.
 */
type TranslateFn = (key: string) => string;

export const buildEmailSchema = (t: TranslateFn) =>
  z
    .string()
    .trim()
    .min(1, t("registrationEmail_required"))
    .superRefine((email, ctx) => {
      if (SPECIAL_CHAR_REGEX.test(email)) {
        ctx.addIssue({code: "custom", message: t("registrationEmail_specialChar")});
        return;
      }
      if (!EMAIL_REGEX.test(email)) {
        ctx.addIssue({code: "custom", message: t("registrationEmail_message")});
        return;
      }
      const [localPart, domainPart] = email.split("@");
      if (!localPart || !domainPart) {
        ctx.addIssue({code: "custom", message: t("registrationEmail_message")});
        return;
      }
      if (
        localPart.startsWith(".") ||
        localPart.endsWith(".") ||
        localPart.startsWith("-") ||
        localPart.startsWith("+") ||
        localPart.endsWith("-") ||
        localPart.endsWith("+")
      ) {
        ctx.addIssue({code: "custom", message: t("registrationEmail_CharStartEnd")});
        return;
      }
      if (
        localPart.includes("..") ||
        localPart.includes("--") ||
        domainPart.includes("..") ||
        domainPart.includes("--")
      ) {
        ctx.addIssue({code: "custom", message: t("registrationEmail_CharConsecutive")});
        return;
      }
      for (const part of domainPart.split(".")) {
        if (part.startsWith("-") || part.endsWith("-")) {
          ctx.addIssue({code: "custom", message: t("registrationEmail_CharStartEnd")});
          return;
        }
      }
    });

/** Returns how many full years have passed between fromISO and toISO. */
export const yearsBetween = (fromISO: string | number | Date, toISO: string | number | Date) => {
  const from = new Date(fromISO);
  const to = new Date(toISO);
  let age = to.getFullYear() - from.getFullYear();
  const m = to.getMonth() - from.getMonth();
  if (m < 0 || (m === 0 && to.getDate() < from.getDate())) age -= 1;
  return age;
};
